import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Bot, Cpu } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { cn } from "@/lib/utils";
import { agentRoleLabel } from "@/lib/agent-display";
import { runNodeBadgeClasses, runNodeBadgeLabel, runNodeDotClasses, runNodeFrameClasses, runNodeGlowClasses } from "@/components/workflows/node-theme";

/**
 * Agent node — calls one of the tenant's agents with a prompt template.
 * One target (left) + one source (right). Blue accent.
 */
export function AgentNode({ data, selected }: NodeProps) {
  const d = data as {
    label?: string;
    agentId?: string;
    description?: string;
    model?: string;
    fallbackModel?: string;
    runStatus?: string;
    runSelected?: boolean;
  };
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const agent = d.agentId ? agents.find((a) => a.id === d.agentId) : undefined;
  const runFrame = runNodeFrameClasses(d.runStatus);
  const runDot = runNodeDotClasses(d.runStatus);
  const runGlow = runNodeGlowClasses(d.runStatus);
  const runBadge = runNodeBadgeLabel(d.runStatus);
  const runBadgeClasses = runNodeBadgeClasses(d.runStatus);

  return (
    <div
      className={cn(
        "relative w-[240px] rounded-xl border bg-surface shadow-sm transition-all duration-300",
        runFrame ?? (selected ? "border-accent shadow-accent/20" : "border-border"),
        d.runSelected && "outline outline-2 outline-offset-4 outline-accent/70",
      )}
    >
      {runGlow && (
        <span className={cn("pointer-events-none absolute -inset-3 z-0 rounded-[1.35rem]", runGlow)} />
      )}
      {runDot && (
        <span className={cn("absolute right-2 top-2 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-surface", runDot)} />
      )}
      {runBadge && runBadgeClasses && (
        <span className={cn("absolute -right-2 -top-3 z-20 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide", runBadgeClasses)}>
          {runBadge}
        </span>
      )}
      <div className="flex items-center gap-1.5 bg-accent/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-accent">
        <Bot size={11} />
        Agent
      </div>
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
          {agent?.avatarUrl ? (
            <img src={agent.avatarUrl} alt="" className="h-8 w-8 rounded-lg object-cover" />
          ) : (
            <Bot size={14} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-text-primary">
            {d.label || agent?.name || "Agent"}
          </p>
          <p className="mt-0.5 line-clamp-2 text-[11px] text-text-muted">
            {d.description || (agent ? agentRoleLabel(agent) : "Select an agent")}
          </p>
          {d.model ? (
            <p className="mt-1 flex items-center gap-1 truncate font-mono text-[10px] text-text-muted">
              <Cpu size={10} className="shrink-0" />
              <span className="truncate">
                {d.model}
                {d.fallbackModel ? ` → ${d.fallbackModel}` : ""}
              </span>
            </p>
          ) : null}
        </div>
      </div>
      <Handle
        type="target"
        position={Position.Left}
        className="!h-2.5 !w-2.5 !border-2 !border-surface !bg-accent"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-2.5 !w-2.5 !border-2 !border-surface !bg-accent"
      />
    </div>
  );
}
